/**
 * The parameter catalogue.
 *
 * Unlike the lake and stream programs, comprehensive water quality monitoring
 * has no single pass/fail number — it measures a dozen things on unrelated
 * scales, on lakes and rivers alike. So the user picks a parameter and that
 * choice drives both the chart and the map colouring.
 *
 * Each parameter declares how it should be judged:
 *
 *   "threshold"  a published standard exists, so values are classified
 *                against it and the source is named in the UI.
 *   "band"       good inside a range, poor outside it (pH).
 *   "relative"   no applicable standard. Values are split into terciles
 *                across the stations currently shown and labelled as
 *                lower/middle/upper third. This is a comparison, never a
 *                judgement, and the UI says so.
 *
 * `field` is the Airtable column name verbatim, including its typos
 * ("flouride_mgl") and stray spaces ("phosphate _phosphorus_ugl"). Those are
 * the real keys; renaming them here would silently break the pull.
 */
export type Judgement = "threshold" | "band" | "relative";

export interface Param {
  key: string;
  field: string;
  label: string;
  short: string;
  unit: string;
  digits: number;
  judgement: Judgement;
  /** For "threshold": ascending cut-points. `worseAbove` says which end is bad. */
  cuts?: number[];
  worseAbove?: boolean;
  /** For "band": the acceptable range. */
  range?: [number, number];
  /** Named in the UI next to the threshold, so a reader can check it. */
  source?: string;
  note?: string;
}

const CATALOGUE: Param[] = [
  {
    key: "tp", field: "total_phosphorus_ugl",
    label: "Total phosphorus", short: "Total P", unit: "µg/L", digits: 1,
    judgement: "threshold", cuts: [10, 20], worseAbove: true,
    source: "Carlson trophic boundaries",
    note: "Below 10 µg/L is oligotrophic; above 20 µg/L is generally eutrophic.",
  },
  {
    key: "tn", field: "total_nitrogen_ugl",
    label: "Total nitrogen", short: "Total N", unit: "µg/L", digits: 0,
    judgement: "relative",
  },
  {
    key: "no3", field: "nitrate_nitrogen_ugl",
    label: "Nitrate nitrogen", short: "Nitrate-N", unit: "µg/L", digits: 1,
    judgement: "relative",
  },
  {
    key: "cl", field: "chloride_mgl",
    label: "Chloride", short: "Chloride", unit: "mg/L", digits: 1,
    // The Council publishes one aquatic maximum rather than a graded pair, so
    // the "fair" band is the approach to it: within 25% of the limit.
    judgement: "threshold", cuts: [240, 320], worseAbove: true,
    source: "Council criterion — aquatic maximum 320 mg/L",
    note: "Rising chloride usually tracks road salt and other developed-land runoff.",
  },
  {
    key: "do", field: "dissolved_oxygen_mgl",
    label: "Dissolved oxygen", short: "DO", unit: "mg/L", digits: 2,
    judgement: "threshold", cuts: [5, 7], worseAbove: false,
    source: "Michigan minimum DO: 7 mg/L coldwater, 5 mg/L warmwater",
    note: "Here lower is worse. Bottom samples are naturally lower than surface.",
  },
  {
    key: "ph", field: "ph",
    label: "pH", short: "pH", unit: "", digits: 2,
    judgement: "band", range: [6.0, 9.0],
    source: "Council criterion for Northern Michigan surface waters, pH 6.0–9.0",
  },
  {
    key: "cond", field: "specific_conductivity_uscm",
    label: "Specific conductivity", short: "Sp. conductivity", unit: "µS/cm", digits: 0,
    judgement: "band", range: [150, 800],
    source: "Council criterion — healthy range 150–800 µS/cm for Northern Michigan surface waters",
    note: "Conductivity and chloride both track human disturbance in a watershed.",
  },
  {
    key: "temp", field: "temp_c",
    label: "Temperature", short: "Temp", unit: "°C", digits: 1,
    judgement: "relative",
    note: "Strongly seasonal and depth-dependent — compare like with like.",
  },
  {
    key: "secchi", field: "secchi_depth_ft",
    label: "Secchi depth", short: "Secchi", unit: "ft", digits: 1,
    judgement: "relative",
  },
  {
    key: "so4", field: "sulfate_mgl",
    label: "Sulfate", short: "Sulfate", unit: "mg/L", digits: 2, judgement: "relative",
  },
  {
    key: "f", field: "flouride_mgl",
    label: "Fluoride", short: "Fluoride", unit: "mg/L", digits: 3, judgement: "relative",
  },
  {
    key: "br", field: "bromide_mgl",
    label: "Bromide", short: "Bromide", unit: "mg/L", digits: 3, judgement: "relative",
  },
  {
    key: "no2", field: "nitrite_mgl",
    label: "Nitrite", short: "Nitrite", unit: "mg/L", digits: 3, judgement: "relative",
  },
  {
    key: "nh4", field: "nh4n_ugl",
    label: "Ammonium nitrogen", short: "Ammonium-N", unit: "µg/L", digits: 1, judgement: "relative",
  },
  {
    key: "po4", field: "phosphate _phosphorus_ugl",
    label: "Phosphate phosphorus", short: "Phosphate-P", unit: "µg/L", digits: 1, judgement: "relative",
  },
];

/**
 * Presentation order is alphabetical by label, everywhere the parameters are
 * listed — the filter dropdown, the chart ribbon and the station panel grid.
 * The catalogue above is grouped by how each is judged, which is useful when
 * editing it and useless when hunting for "Chloride" in a list of fifteen.
 */
export const PARAMS: Param[] = [...CATALOGUE].sort((a, b) =>
  a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
);

/*
 * The chart ribbon and the station panel render `short`, not `label`, so the
 * two have to agree on where a parameter sorts — otherwise one list reads A–Z
 * and the other does not, side by side on the same screen. "Specific
 * conductivity" is abbreviated "Sp. conductivity" rather than "Conductivity"
 * for exactly that reason. This check keeps the next edit honest.
 */
if (import.meta.env?.DEV) {
  const byLabel = PARAMS.map((p) => p.short);
  const byShort = [...byLabel].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" }),
  );
  if (byLabel.join("|") !== byShort.join("|")) {
    console.warn(
      "[params] short labels do not sort the same as full labels; one of the " +
      "lists will look unsorted:\n  by label: " + byLabel.join(", ") +
      "\n  by short: " + byShort.join(", "),
    );
  }
}

/**
 * What the app opens on. Named explicitly rather than taken from PARAMS[0]:
 * the list is ordered for the reader, and that ordering must not decide what
 * gets shown first. Alphabetically PARAMS[0] is ammonium nitrogen, which has
 * 122 samples; total phosphorus has 1,546 and a published standard.
 */
export const DEFAULT_PARAM = "tp";

export const PARAM_BY_KEY = new Map(PARAMS.map((p) => [p.key, p]));

/* ---------- classification ---------- */

export type Rating = "good" | "fair" | "poor" | "none";

export const RATING: Record<Rating, { label: string; color: string; soft: string }> = {
  good: { label: "Good",    color: "#1A7A3C", soft: "#E7F3EA" },
  fair: { label: "Fair",    color: "#C99700", soft: "#FBF3DF" },
  poor: { label: "Poor",    color: "#A4161A", soft: "#FAE9EA" },
  none: { label: "No data", color: "#8A93A6", soft: "#F0F2F6" },
};

/** Neutral three-step scale for relative parameters, so a comparison never
 *  wears the colours of a judgement. */
export const RELATIVE: Record<Rating, { label: string; color: string; soft: string }> = {
  good: { label: "Lower third",  color: "#9DC6DD", soft: "#EDF5FA" },
  fair: { label: "Middle third", color: "#2E86B0", soft: "#E3EFF6" },
  poor: { label: "Upper third",  color: "#0B3C5D", soft: "#E1E7EC" },
  none: { label: "No data",      color: "#8A93A6", soft: "#F0F2F6" },
};

export function scaleFor(p: Param) {
  return p.judgement === "relative" ? RELATIVE : RATING;
}

/**
 * Classify one value. `terciles` are only consulted for relative parameters
 * and are computed across whatever stations are currently in view, so the
 * scale re-bases when a waterbody filter narrows the set.
 */
export function classify(
  p: Param,
  v: number | null | undefined,
  terciles?: [number, number] | null,
): Rating {
  if (v === null || v === undefined || !Number.isFinite(v)) return "none";

  if (p.judgement === "band" && p.range) {
    const [lo, hi] = p.range;
    if (v >= lo && v <= hi) return "good";
    // Just outside a standard is "fair"; well outside is "poor".
    const margin = (hi - lo) * 0.15;
    return v >= lo - margin && v <= hi + margin ? "fair" : "poor";
  }

  if (p.judgement === "threshold" && p.cuts) {
    const [a, b] = p.cuts;
    if (p.worseAbove) return v <= a ? "good" : v <= b ? "fair" : "poor";
    return v >= b ? "good" : v >= a ? "fair" : "poor";
  }

  if (!terciles) return "none";
  const [t1, t2] = terciles;
  return v <= t1 ? "good" : v <= t2 ? "fair" : "poor";
}

/** Tercile cut-points of the supplied values, or null if too few to split. */
export function terciles(values: number[]): [number, number] | null {
  const v = values.filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  if (v.length < 6) return null;
  const at = (q: number) => v[Math.min(v.length - 1, Math.floor(q * v.length))];
  return [at(1 / 3), at(2 / 3)];
}
